import { useState, useRef } from 'react';

export default function BlueprintUpload() {
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      setUploadedFile(files[0]);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      setUploadedFile(files[0]);
    }
  };

  return (
    <section id="blueprint" className="py-24 bg-white">
      <div className="max-w-[1400px] mx-auto px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <p className="text-[11px] tracking-[0.3em] uppercase text-[#4A90E2] mb-4 font-light">
              PROJECT PLANNING
            </p>
            <h2 className="text-5xl font-serif text-gray-900 mb-6">
              <strong>Upload Your Blueprint</strong>
            </h2>
            <p className="text-base text-gray-600 leading-relaxed max-w-2xl mx-auto">
              Share your floor plans or blueprints with us for a detailed <strong>home automation</strong> proposal. We'll analyze your space and recommend the optimal <strong>Control4, Savant, RTI, or Brilliant</strong> configuration.
            </p>
          </div>

          {/* Upload Area */}
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`relative border-2 border-dashed rounded-2xl p-12 transition-all cursor-pointer ${
              isDragging
                ? 'border-[#4A90E2] bg-[#4A90E2]/5'
                : 'border-gray-300 hover:border-[#4A90E2] hover:bg-gray-50'
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.dwg"
              onChange={handleFileSelect}
              className="hidden"
            />

            <div className="text-center">
              {uploadedFile ? (
                <>
                  <div className="w-20 h-20 rounded-full bg-[#4A90E2]/10 flex items-center justify-center mx-auto mb-6">
                    <i className="ri-file-check-line text-4xl text-[#4A90E2]"></i>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    File Uploaded Successfully
                  </h3>
                  <p className="text-sm text-gray-600 mb-6">
                    {uploadedFile.name} ({(uploadedFile.size / 1024 / 1024).toFixed(2)} MB)
                  </p>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setUploadedFile(null);
                    }}
                    className="px-6 py-3 bg-gray-100 text-gray-700 text-sm font-medium rounded-full hover:bg-gray-200 transition-colors whitespace-nowrap"
                  >
                    Upload Different File
                  </button>
                </>
              ) : (
                <>
                  <div className="w-20 h-20 rounded-full bg-[#4A90E2]/10 flex items-center justify-center mx-auto mb-6">
                    <i className="ri-upload-cloud-line text-4xl text-[#4A90E2]"></i>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    Drop your files here
                  </h3>
                  <p className="text-sm text-gray-600 mb-6">
                    or click to browse from your device
                  </p>
                  <div className="flex items-center justify-center gap-4 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <i className="ri-file-pdf-line"></i>
                      PDF
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-image-line"></i>
                      JPG/PNG
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-file-line"></i>
                      DWG
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Features */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            <div className="text-center p-6 bg-gray-50 rounded-xl">
              <div className="w-12 h-12 rounded-full bg-[#4A90E2]/10 flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-[#4A90E2] text-xl"></i>
              </div>
              <h4 className="text-sm font-semibold text-gray-900 mb-2">Secure Upload</h4>
              <p className="text-xs text-gray-600">
                Your files are encrypted and protected
              </p>
            </div>

            <div className="text-center p-6 bg-gray-50 rounded-xl">
              <div className="w-12 h-12 rounded-full bg-[#4A90E2]/10 flex items-center justify-center mx-auto mb-4">
                <i className="ri-time-line text-[#4A90E2] text-xl"></i>
              </div>
              <h4 className="text-sm font-semibold text-gray-900 mb-2">Fast Response</h4>
              <p className="text-xs text-gray-600">
                Receive proposal within 24-48 hours
              </p>
            </div>

            <div className="text-center p-6 bg-gray-50 rounded-xl">
              <div className="w-12 h-12 rounded-full bg-[#4A90E2]/10 flex items-center justify-center mx-auto mb-4">
                <i className="ri-user-star-line text-[#4A90E2] text-xl"></i>
              </div>
              <h4 className="text-sm font-semibold text-gray-900 mb-2">Expert Analysis</h4>
              <p className="text-xs text-gray-600">
                Reviewed by certified technicians
              </p>
            </div>
          </div>

          {/* CTA */}
          {uploadedFile && (
            <div className="mt-12 text-center">
              <a
                href="#contact"
                className="inline-flex items-center gap-2 px-8 py-4 bg-[#4A90E2] text-white text-sm font-medium tracking-wide rounded-full hover:bg-[#3A7BC8] hover:shadow-lg hover:shadow-[#4A90E2]/30 transition-all cursor-pointer whitespace-nowrap"
              >
                Continue to Contact Form
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
